import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_BFF_WEB_URL } from "utilities/Constant";

export interface RegisterData {
  emailaddress: string;
  password: string;
  firstname: string;
  lastname: string;
  invitationId?: string;
  loanPurpose: string;
  phoneNumber: string;
  referralSource?: string;
  referredBy?: string;
  inquiryId?: string;
  status?: string;
  leadProvider?: LeadProvider;
  nhDealApplicationId?: string;
  userRole?: RegistrationRoleType;
  isTestLoan?: boolean;
  decryptPhoneNumber?: string;
}

export interface RegisterResponse {
  name: string;
  id: string;
  accessToken: string;
  status: string;
  refreshToken: string;
  version: string;
  inquiryId: string;
  dealApplicationId: string;
  userRole: string;
  deviceAuthId: string;
}

export interface DecryptedUser {
  id: string;
  fn: string;
  ln: string;
  em: string;
  ph: string;
  lp: string;
  ex: boolean;
  lpn: LeadProvider;
  nhDealAppId: string;
  hp: boolean;
  rt: RegistrationRoleType;
  isTestLoan: boolean;
}

interface DecryptedEmail {
  em: string;
  ex: boolean;
  hp: boolean;
  nhDealAppId: string;
}

export interface SetPasswordRequest {
  email: string;
  password: string;
  nhDealApplicationId: string;
  phoneNumber?: string;
}

interface SetPasswordResponse {
  userId: string;
  userLastName: string;
  deviceAuthId: string;
}

export interface UserPasswordResponse {
  user: {
    id: string;
    lastName: string;
  };
  deviceId?: string | null;
}

export enum LeadProvider {
  Default = "",
  DigitalLeads = "Digital",
  Microsite = "Microsite",
  Bonzo = "Bonzo",
  NafLink = "NAFLink",
}

export enum RegistrationRoleType {
  Unknown = "Unknown",
  Borrower = "Borrower",
  SpousalCoBorrower = "SpousalCoBorrower",
  NonSpousalCoBorrower = "NonSpousalCoBorrower",
}

export const registrationApi = createApi({
  reducerPath: "registrationApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_BFF_WEB_URL, credentials: "include" }),
  endpoints: (build) => ({
    postRegistration: build.mutation<RegisterResponse, RegisterData>({
      query: (body: RegisterData) => ({
        url: "/Account/register",
        method: "POST",
        body,
      }),
    }),
    getDecryptUser: build.query<
      DecryptedUser,
      { encryptedUser?: string; email?: string; loanId?: string; leadProviderName?: string; dealId?: string; losId?: string; prospectId?: string }
    >({
      query: ({ encryptedUser, email, loanId, leadProviderName, dealId, losId, prospectId }) => {
        const queryParams: Record<string, string> = {};
        if (encryptedUser !== undefined) queryParams["t"] = encryptedUser ?? "";
        if (email !== undefined) queryParams["email"] = email ?? "";
        if (loanId !== undefined) queryParams["loanId"] = loanId ?? "";
        if (leadProviderName !== undefined) queryParams["leadProviderName"] = leadProviderName ?? "";
        if (dealId !== undefined) queryParams["dealId"] = dealId ?? "";
        if (losId !== undefined) queryParams["losId"] = losId ?? "";
        if (prospectId !== undefined) queryParams["prospectId"] = prospectId ?? "";
        const queryString = new URLSearchParams(queryParams).toString();
        return `/user?${queryString}`;
      },
    }),
    getDecryptEmail: build.query<DecryptedEmail, string>({
      query: (encryptedUser: string) => `/user/email?t=${encryptedUser}`,
    }),
    PatchSetPassword: build.mutation<SetPasswordResponse, SetPasswordRequest>({
      query: (body: SetPasswordRequest) => ({
        url: "/Account/setpassword",
        method: "PATCH",
        body,
      }),
    }),
    setPasswordWithoutDeviceAuth: build.mutation<UserPasswordResponse, SetPasswordRequest>({
      query: (body: SetPasswordRequest) => ({
        url: "/Account/password",
        method: "PATCH",
        body,
      }),
    }),
    getUserDetails: build.query<boolean, string>({
      query: (Email: string) => ({
        url: `/user/phone-required?emailAddress=${encodeURIComponent(Email)}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  usePostRegistrationMutation,
  useGetDecryptUserQuery,
  usePatchSetPasswordMutation,
  useGetDecryptEmailQuery,
  useSetPasswordWithoutDeviceAuthMutation,
  useGetUserDetailsQuery,
} = registrationApi;
